import { ApiResponse, APIError } from '@/types/api';

// Utility functions for API response handling
export const APIUtils = {
  // Check if response is successful
  isSuccess<T>(response: ApiResponse<T>): response is ApiResponse<T> & { data: T } {
    return !response.error && response.status >= 200 && response.status < 300;
  },

  // Check if response has error
  hasError<T>(response: ApiResponse<T>): response is ApiResponse<T> & { error: APIError } {
    return !!response.error;
  },

  // Extract error message
  getErrorMessage<T>(response: ApiResponse<T>): string {
    if (response.error) {
      return response.error.details || response.error.error;
    }
    return 'Unknown error';
  },

  // Format response time
  formatResponseTime(ms: number): string {
    if (ms < 1000) {
      return `${ms}ms`;
    }
    return `${(ms / 1000).toFixed(2)}s`;
  },

  // Format memory size
  formatMemorySize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(2)} ${units[unitIndex]}`;
  },

  // Format timestamp
  formatTimestamp(timestamp: number | string): string {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : new Date(timestamp);
    return date.toLocaleString();
  },

  // Get status color based on health
  getHealthColor(status: string): string {
    switch (status) {
      case 'healthy':
      case 'operational':
        return 'text-green-600 bg-green-100';
      case 'unhealthy':
      case 'down':
        return 'text-red-600 bg-red-100';
      case 'degraded':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  },

  // Get complexity color
  getComplexityColor(complexity: string): string {
    switch (complexity) {
      case 'low':
        return 'text-green-600 bg-green-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'high':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  },

  // Get compatibility color
  getCompatibilityColor(compatibility: number): string {
    if (compatibility >= 0.9) {
      return 'text-green-600 bg-green-100';
    } else if (compatibility >= 0.7) {
      return 'text-yellow-600 bg-yellow-100';
    } else {
      return 'text-red-600 bg-red-100';
    }
  },

  // Get migration complexity color
  getMigrationComplexityColor(complexity: string): string {
    switch (complexity) {
      case 'low':
        return 'text-green-600 bg-green-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'high':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  },

  // Format percentage
  formatPercentage(value: number): string {
    return `${(value * 100).toFixed(1)}%`;
  },

  // Format cost
  formatCost(cost: number): string {
    if (cost < 0.01) {
      return `${(cost * 1000).toFixed(1)}¢`;
    }
    return `$${cost.toFixed(4)}`;
  },

  // Validate project path
  isValidProjectPath(path: string): boolean {
    return /^\/[a-zA-Z0-9\-_\/]*$/.test(path) && path.length > 0;
  },

  // Validate ecosystem
  isValidEcosystem(ecosystem: string): boolean {
    const validEcosystems = ['npm', 'maven', 'pip', 'cargo', 'go', 'nuget'];
    return validEcosystems.includes(ecosystem.toLowerCase());
  },

  // Validate task type
  isValidTaskType(type: string): boolean {
    const validTypes = ['code-generation', 'reasoning', 'translation', 'analysis', 'optimization'];
    return validTypes.includes(type);
  },

  // Validate complexity
  isValidComplexity(complexity: string): boolean {
    const validComplexities = ['low', 'medium', 'high'];
    return validComplexities.includes(complexity);
  },

  // Create retry function
  async retry<T>(
    fn: () => Promise<T>,
    maxAttempts: number = 3,
    delay: number = 1000
  ): Promise<T> {
    let lastError: Error;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error as Error;
        console.warn(`Attempt ${attempt} failed:`, lastError.message);

        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
      }
    }

    throw lastError!;
  },

  // Debounce function
  debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  },

  // Throttle function
  throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void {
    let inThrottle: boolean;
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  },
};

export default APIUtils;